import React, { useState } from 'react';
import { Card, Skeleton, Space } from 'antd';
import { useRequest } from 'ahooks';
import { ResponsiveBar } from '@nivo/bar';
import { getModelConsumption } from '../../services/dashboardService.js';
import dayjs from 'dayjs';

export default function ModelConsumptionCard({ dateParams }) {
  const [hiddenKeys, setHiddenKeys] = useState([]);

  const params = React.useMemo(() => ({ ...(dateParams || {}) }), [dateParams]);

  const { data: resp, loading } = useRequest(
    () => getModelConsumption(params),
    { refreshDeps: [params.start, params.end, params.companyId] }
  );

  const apiData = React.useMemo(() => resp?.data || resp || {}, [resp]);
  const allKeys = React.useMemo(() => apiData?.keys || [], [apiData]);
  const rows = React.useMemo(() => apiData?.rows || [], [apiData]);

  const allHidden = React.useMemo(() => {
    if (!allKeys.length) return false;
    return allKeys.every(k => hiddenKeys.includes(k));
  }, [allKeys, hiddenKeys]);

  const barData = React.useMemo(() => {
    return rows.map(row => {
      const r = { ...row };
      allKeys.forEach(k => {
        if (hiddenKeys.includes(k)) r[k] = 0;
      });
      return r;
    });
  }, [rows, allKeys, hiddenKeys]);

  /** time span */
  const dayDiff = React.useMemo(() => {
    if (!params?.start || !params?.end) return rows.length;
    return Math.max(
      1,
      dayjs(params.end).diff(dayjs(params.start), 'day') + 1
    );
  }, [params?.start, params?.end, rows.length]);

  /** sampling */
  const sampledRows = React.useMemo(() => {
    if (!Array.isArray(barData) || barData.length === 0) return [];
    if (dayDiff <= 7) return barData;

    const targetBars = 10;
    if (barData.length <= targetBars) return barData;

    const step = Math.ceil(barData.length / targetBars);
    const arr = barData.filter((_, i) => i % step === 0);
    if (arr[arr.length - 1]?.date !== barData[barData.length - 1]?.date) {
      arr.push(barData[barData.length - 1]);
    }
    return arr;
  }, [barData, dayDiff]);

  const palette = [
    '#69b1ff', '#95de64', '#b37feb', '#ffd666',
    '#ff85c0', '#5cdbd3', '#ffc069', '#73d13d',
    '#9254de', '#40a9ff',
  ];

  const colors = React.useMemo(() => {
    const map = {};
    allKeys.forEach((k, i) => (map[k] = palette[i % palette.length]));
    return map;
  }, [allKeys]);

  const toggleKey = (key) => {
    setHiddenKeys(prev =>
      prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };


  const Legend = () => (
    <Space size={10} style={{ fontSize: 11 }}>
      {allKeys.map(k => {
        const hidden = hiddenKeys.includes(k);
        return (
          <span
            key={k}
            onClick={() => toggleKey(k)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              opacity: hidden ? 0.5 : 1,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: hidden ? '#d9d9d9' : colors[k],
              }}
            />
            <span
              style={{
                color: hidden ? '#bfbfbf' : '#333',
                textDecoration: hidden ? 'line-through' : 'none',
              }}
            >
              {k}
            </span>
          </span>
        );
      })}
    </Space>
  );

  return (
    <Card
      title={<span style={{ fontSize: 14, fontWeight: 600 }}>Model Consumption</span>}
      extra={<Legend />}
    >
      {loading ? (
        <Skeleton active />
      ) : allHidden ? (
        /** hide all */
        <div
          style={{
            height: 240,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: 16,
          }}
        >
          No data visible
        </div>
      ) : (
        <div style={{ height: 240 }}>
          <ResponsiveBar
            data={sampledRows}
            keys={allKeys}
            indexBy="date"
            groupMode="stacked"
            padding={0.5}
            margin={{ top: 30, right: 10, bottom: 60, left: 50 }}

            colors={({ id }) =>
              hiddenKeys.includes(id) ? '#d9d9d9' : colors[id]
            }
            fillOpacity={({ id }) =>
              hiddenKeys.includes(id) ? 0.2 : 1
            }

            enableLabel={false}
            enableGridX={false}
            enableGridY={false}

            axisBottom={{
              tickRotation: -45,
              format: v => dayjs(v).format('MM-DD'),
            }}
            axisLeft={{ tickValues: 6 }}

            /** Click on the bar chart , Segment switching hide */
            onClick={(bar) => toggleKey(bar.id)}
          />
        </div>
      )}
    </Card>
  );
}
